import { useEffect, useMemo, useState } from 'react';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Divider,
    LinearProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
} from '@mui/material';
import { Title } from 'react-admin';

import { DesktopActionCell, DesktopActionHeaderCell } from '../admin/DesktopTableActions';
import { SegmentedTabs, type SegmentedTabOption } from '../admin/SegmentedTabs';
import { examTaskCategories } from '../examTasks/examTaskTypes';
import { formatApiDateTime } from '../admin/dateTimeUtils';
import {
    getMockTestScoreReviewList,
    type MockTestScoreReviewAttemptDto,
} from '../admin/adminMockTestScoreReviewApi';

type FilterState = {
    search: string;
    dateFrom: string;
    dateTo: string;
    userEmail: string;
    mockTestIds: string;
    userIds: string;
    mockTestTitle: string;
    examName: string;
    mockTestKind: string;
    sectionCategory: string;
    status: string;
};

type SummaryMetricProps = {
    label: string;
    value: string;
    helper?: string;
};

type SortField = 'attemptedAt' | 'lastAttemptedAt';

type SortOrder = 'ASC' | 'DESC';

const emptyFilters: FilterState = {
    search: '',
    dateFrom: '',
    dateTo: '',
    userEmail: '',
    mockTestIds: '',
    userIds: '',
    mockTestTitle: '',
    examName: '',
    mockTestKind: '',
    sectionCategory: '',
    status: '',
};

const statusLabelByValue: Record<string, string> = {
    inprogress: 'In Progress',
    completed: 'Completed',
};

function normalizeStatus(value: string): string {
    return value.trim().replace(/[_\s-]+/g, '').toLowerCase();
}

function formatAttemptStatus(value: string): string {
    const normalized = normalizeStatus(value);
    return statusLabelByValue[normalized] ?? (value.trim() || 'Unknown');
}

function getAttemptStatusChipColor(value: string): 'default' | 'warning' | 'success' {
    const normalized = normalizeStatus(value);

    if (normalized === 'inprogress') {
        return 'warning';
    }

    if (normalized === 'completed') {
        return 'success';
    }

    return 'default';
}

function getAttemptActionLabel(value: string): string {
    return normalizeStatus(value) === 'inprogress' ? 'View Progress' : 'View Review';
}

function formatDateTime(value: string): string {
    return formatApiDateTime(value);
}

function parseIdentifierList(value: string): string[] {
    return value
        .split(/[\s,]+/)
        .map((entry) => entry.trim())
        .filter(Boolean);
}

function toBoundaryIso(value: string, endOfDay: boolean): string | undefined {
    if (!value) return undefined;

    const time = endOfDay ? '23:59:59.999' : '00:00:00.000';
    const date = new Date(`${value}T${time}`);
    return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

function getAppliedFilterCount(filters: FilterState): number {
    return Object.values(filters).filter((value) => value.trim()).length;
}

function getScoredExamTaskCount(attempts: MockTestScoreReviewAttemptDto[]): number {
    return attempts.reduce(
        (sum, attempt) => sum + attempt.examTasks.filter((examTask) => examTask.score !== null).length,
        0,
    );
}

function isSectionalMockTestKind(mockTestKind: string): boolean {
    return mockTestKind.trim().toLowerCase() === 'sectional';
}

function getAttemptSectionCategories(attempt: MockTestScoreReviewAttemptDto): string[] {
    return Array.from(new Set(attempt.examTasks.map((examTask) => examTask.examTaskCategory).filter(Boolean)));
}

function renderAttemptSectionCategoryChips(attempt: MockTestScoreReviewAttemptDto) {
    return getAttemptSectionCategories(attempt).map((category) => (
        <Chip key={`${attempt.mockTestAttemptId}-${category}`} label={category} size="small" variant="outlined" />
    ));
}

function SummaryMetric({ label, value, helper }: SummaryMetricProps) {
    return (
        <Card
            sx={{
                borderRadius: 4,
                border: '1px solid rgba(148, 163, 184, 0.18)',
                background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)',
                boxShadow: '0 18px 40px rgba(15, 23, 42, 0.08)',
            }}
        >
            <CardContent>
                <Typography variant="overline" sx={{ color: '#64748b', fontWeight: 800, letterSpacing: '0.12em' }}>
                    {label}
                </Typography>
                <Typography variant="h5" sx={{ color: '#0f172a', fontWeight: 800 }}>
                    {value}
                </Typography>
                {helper ? (
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                        {helper}
                    </Typography>
                ) : null}
            </CardContent>
        </Card>
    );
}

export function MockTestScoreReviewPage() {
    const [filters, setFilters] = useState<FilterState>(emptyFilters);
    const [appliedFilters, setAppliedFilters] = useState<FilterState>(emptyFilters);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [reloadToken, setReloadToken] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [attempts, setAttempts] = useState<MockTestScoreReviewAttemptDto[]>([]);
    const [total, setTotal] = useState(0);
    const [sortField, setSortField] = useState<SortField>('lastAttemptedAt');
    const [sortOrder, setSortOrder] = useState<SortOrder>('DESC');

    useEffect(() => {
        let active = true;

        setLoading(true);
        setError('');

        const useSectionCategory = isSectionalMockTestKind(appliedFilters.mockTestKind);

        void getMockTestScoreReviewList({
            page: page + 1,
            pageSize: rowsPerPage,
            search: appliedFilters.search.trim() || undefined,
            dateFrom: toBoundaryIso(appliedFilters.dateFrom, false),
            dateTo: toBoundaryIso(appliedFilters.dateTo, true),
            userEmail: appliedFilters.userEmail.trim() || undefined,
            mockTestIds: parseIdentifierList(appliedFilters.mockTestIds),
            userIds: parseIdentifierList(appliedFilters.userIds),
            mockTestTitle: appliedFilters.mockTestTitle.trim() || undefined,
            status: appliedFilters.status.trim() || undefined,
            sectionCategory: useSectionCategory ? appliedFilters.sectionCategory.trim() || undefined : undefined,
            sort: sortField,
            order: sortOrder,
        })
            .then((result) => {
                if (!active) return;
                setAttempts(result.data);
                setTotal(result.total);
            })
            .catch((fetchError: unknown) => {
                if (!active) return;
                setAttempts([]);
                setTotal(0);
                setError(fetchError instanceof Error ? fetchError.message : 'Unable to load mock test score reviews');
            })
            .finally(() => {
                if (active) {
                    setLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, [appliedFilters, page, rowsPerPage, reloadToken, sortField, sortOrder]);

    const activeFilterCount = useMemo(() => getAppliedFilterCount(appliedFilters), [appliedFilters]);
    const examTabs = useMemo<SegmentedTabOption[]>(
        () => [
            { value: '', label: 'All Exams' },
            { value: 'Core', label: 'Core' },
            { value: 'Academic', label: 'Academic' },
        ],
        [],
    );
    const kindTabs = useMemo<SegmentedTabOption[]>(
        () => [
            { value: '', label: 'All Kinds' },
            { value: 'Sectional', label: 'Sectional' },
            { value: 'Full', label: 'Full' },
        ],
        [],
    );
    const statusTabs = useMemo<SegmentedTabOption[]>(
        () => [
            { value: '', label: 'All Statuses' },
            { value: 'InProgress', label: 'In Progress' },
            { value: 'Completed', label: 'Completed' },
        ],
        [],
    );
    const sectionCategoryTabs = useMemo<SegmentedTabOption[]>(
        () => [{ value: '', label: 'All Categories' }, ...examTaskCategories.map((category) => ({ value: category.id, label: category.label }))],
        [],
    );
    const filtersUseSectionCategory = isSectionalMockTestKind(filters.mockTestKind);
    const appliedFiltersUseSectionCategory = isSectionalMockTestKind(appliedFilters.mockTestKind);
    const visibleAttempts = useMemo(() => {
        return attempts.filter((attempt) => {
            const examMatch = !appliedFilters.examName || attempt.examName === appliedFilters.examName;
            const kindMatch = !appliedFilters.mockTestKind || attempt.mockTestKind === appliedFilters.mockTestKind;
            const statusMatch = !appliedFilters.status || normalizeStatus(attempt.status) === normalizeStatus(appliedFilters.status);
            const categoryMatch = !appliedFiltersUseSectionCategory
                ? true
                : !appliedFilters.sectionCategory || getAttemptSectionCategories(attempt).includes(appliedFilters.sectionCategory);
            return examMatch && kindMatch && statusMatch && categoryMatch;
        });
    }, [appliedFilters.examName, appliedFilters.mockTestKind, appliedFilters.sectionCategory, appliedFilters.status, appliedFiltersUseSectionCategory, attempts]);
    const totalExamTasks = useMemo(
        () => visibleAttempts.reduce((sum, attempt) => sum + attempt.examTasks.length, 0),
        [visibleAttempts],
    );
    const scoredExamTasks = useMemo(() => getScoredExamTaskCount(visibleAttempts), [visibleAttempts]);

    const handleFilterChange = (key: keyof FilterState, value: string) => {
        setFilters((current) => ({ ...current, [key]: value }));
    };

    const handleTabFilterChange = (key: keyof FilterState, value: string) => {
        setPage(0);
        setFilters((current) => ({ ...current, [key]: value }));
        setAppliedFilters((current) => ({
            ...current,
            [key]: value,
            ...(key === 'mockTestKind' && value.trim().toLowerCase() !== 'sectional' ? { sectionCategory: '' } : {}),
            ...(key === 'mockTestKind' && value.trim().toLowerCase() === 'sectional' && !current.sectionCategory
                ? { sectionCategory: 'Speaking' }
                : {}),
        }));
    };

    const applyFilters = () => {
        setPage(0);
        setAppliedFilters(filters);
    };

    const resetFilters = () => {
        setFilters(emptyFilters);
        setAppliedFilters(emptyFilters);
        setPage(0);
    };

    const handleOpenReviewDetail = (mockTestAttemptId: string, userId: string) => {
        const normalizedUserId = userId.trim();
        if (normalizedUserId) {
            sessionStorage.setItem(`mock-test-score-review:user:${mockTestAttemptId}`, normalizedUserId);
        }

        const searchParams = new URLSearchParams();
        if (normalizedUserId) {
            searchParams.set('userId', normalizedUserId);
        }

        const url = new URL(window.location.href);
        url.hash = `#/mock-test-score-review/${mockTestAttemptId}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
        window.open(url.toString(), '_blank', 'noopener,noreferrer');
    };

    const handleSortChange = (field: SortField) => {
        setPage(0);
        if (sortField === field) {
            setSortOrder((current) => (current === 'ASC' ? 'DESC' : 'ASC'));
            return;
        }

        setSortField(field);
        setSortOrder('DESC');
    };

    const renderTimeSortLabel = (field: SortField, label: string) => (
        <TableSortLabel
            active={sortField === field}
            direction={sortField === field && sortOrder === 'ASC' ? 'asc' : 'desc'}
            onClick={() => handleSortChange(field)}
        >
            {label}
        </TableSortLabel>
    );

    return (
        <>
            <Title title="Mock Test Score Review" />
            <Stack spacing={3} sx={{ p: { xs: 2, md: 3 } }}>
                <Card
                    sx={{
                        borderRadius: 5,
                        overflow: 'hidden',
                        color: '#e2e8f0',
                        background:
                            'radial-gradient(circle at top left, rgba(56, 189, 248, 0.35) 0%, rgba(15, 23, 42, 0) 36%), linear-gradient(135deg, #0f172a 0%, #1e293b 52%, #111827 100%)',
                        boxShadow: '0 28px 70px rgba(15, 23, 42, 0.28)',
                    }}
                >
                    <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                        <Stack
                            direction={{ xs: 'column', lg: 'row' }}
                            spacing={3}
                            alignItems={{ xs: 'flex-start', lg: 'center' }}
                            justifyContent="space-between"
                        >
                            <Box>
                                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                                    <AssessmentOutlinedIcon sx={{ color: '#67e8f9' }} />
                                    <Typography variant="overline" sx={{ letterSpacing: '0.16em', fontWeight: 800, color: '#67e8f9' }}>
                                        Review Operations
                                    </Typography>
                                </Stack>
                                <Typography variant="h4" sx={{ fontWeight: 800, maxWidth: 760 }}>
                                    Review attended mock test attempts and nested exam task responses from the live admin DTO.
                                </Typography>
                                <Typography sx={{ mt: 1.25, maxWidth: 780, color: 'rgba(226, 232, 240, 0.82)' }}>
                                    The current endpoint returns attempt metadata plus exam task entries with prompt, answer, score, and system feedback.
                                </Typography>
                            </Box>

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                                <Button
                                    variant="outlined"
                                    startIcon={<RefreshOutlinedIcon />}
                                    onClick={() => setReloadToken((value) => value + 1)}
                                    sx={{
                                        color: '#e2e8f0',
                                        borderColor: 'rgba(103, 232, 249, 0.42)',
                                        '&:hover': { borderColor: '#67e8f9', backgroundColor: 'rgba(103, 232, 249, 0.08)' },
                                    }}
                                >
                                    Refresh
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<SearchOutlinedIcon />}
                                    onClick={applyFilters}
                                    sx={{
                                        background: 'linear-gradient(135deg, #38bdf8 0%, #0ea5e9 100%)',
                                        color: '#082f49',
                                        fontWeight: 800,
                                    }}
                                >
                                    Apply Filters
                                </Button>
                            </Stack>
                        </Stack>
                    </CardContent>
                </Card>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' },
                        gap: 2,
                    }}
                >
                    <SummaryMetric label="Total Attempts" value={String(total)} helper="Server-reported total" />
                    <SummaryMetric label="Loaded Attempts" value={String(visibleAttempts.length)} helper={`Page ${page + 1}`} />
                    <SummaryMetric label="Exam Tasks" value={String(totalExamTasks)} helper="Visible page only" />
                    <SummaryMetric label="Scored Exam Tasks" value={String(scoredExamTasks)} helper={`${activeFilterCount} active filters`} />
                </Box>

                <Card sx={{ borderRadius: 4, border: '1px solid rgba(148, 163, 184, 0.18)' }}>
                    <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                            Filters
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', mb: 2.5 }}>
                            The endpoint currently supports free-text search, date filters, user filters, and mock test title filtering.
                        </Typography>

                        <Stack spacing={2} sx={{ mb: 2.5 }}>
                            <SegmentedTabs
                                label="Exam"
                                value={filters.examName}
                                options={examTabs}
                                onChange={(nextValue) => handleTabFilterChange('examName', nextValue)}
                            />
                            <SegmentedTabs
                                label="Mock Test Kind"
                                value={filters.mockTestKind}
                                options={kindTabs}
                                onChange={(nextValue) => {
                                    handleTabFilterChange('mockTestKind', nextValue);
                                }}
                            />
                            <SegmentedTabs
                                label="Attempt Status"
                                value={filters.status}
                                options={statusTabs}
                                onChange={(nextValue) => handleTabFilterChange('status', nextValue)}
                            />
                            {filtersUseSectionCategory ? (
                                <SegmentedTabs
                                    label="Section Category"
                                    value={filters.sectionCategory}
                                    options={sectionCategoryTabs}
                                    onChange={(nextValue) => handleTabFilterChange('sectionCategory', nextValue)}
                                    helperText="Applies to sectional mock tests only."
                                />
                            ) : null}
                        </Stack>

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' },
                                gap: 2,
                            }}
                        >
                            <TextField
                                label="Search"
                                value={filters.search}
                                onChange={(event) => handleFilterChange('search', event.target.value)}
                                placeholder="Attempt, user, exam task..."
                                fullWidth
                            />
                            <TextField
                                label="User Email"
                                value={filters.userEmail}
                                onChange={(event) => handleFilterChange('userEmail', event.target.value)}
                                placeholder="user@example.com"
                                fullWidth
                            />
                            <TextField
                                label="Mock Test Title"
                                value={filters.mockTestTitle}
                                onChange={(event) => handleFilterChange('mockTestTitle', event.target.value)}
                                placeholder="Academic Full Mock Test"
                                fullWidth
                            />
                            <TextField
                                label="Mock Test IDs"
                                value={filters.mockTestIds}
                                onChange={(event) => handleFilterChange('mockTestIds', event.target.value)}
                                placeholder="Comma separated UUIDs"
                                fullWidth
                            />
                            <TextField
                                label="User IDs"
                                value={filters.userIds}
                                onChange={(event) => handleFilterChange('userIds', event.target.value)}
                                placeholder="Comma separated UUIDs"
                                fullWidth
                            />
                            <TextField
                                label="Date From"
                                type="date"
                                value={filters.dateFrom}
                                onChange={(event) => handleFilterChange('dateFrom', event.target.value)}
                                fullWidth
                                slotProps={{ inputLabel: { shrink: true } }}
                            />
                            <TextField
                                label="Date To"
                                type="date"
                                value={filters.dateTo}
                                onChange={(event) => handleFilterChange('dateTo', event.target.value)}
                                fullWidth
                                slotProps={{ inputLabel: { shrink: true } }}
                            />
                        </Box>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2.5 }}>
                            <Button variant="contained" onClick={applyFilters} startIcon={<SearchOutlinedIcon />}>
                                Apply
                            </Button>
                            <Button variant="text" onClick={resetFilters}>
                                Reset
                            </Button>
                        </Stack>
                    </CardContent>
                </Card>

                <Card sx={{ borderRadius: 4, border: '1px solid rgba(148, 163, 184, 0.18)', overflow: 'hidden' }}>
                    {loading ? <LinearProgress /> : null}
                    <CardContent sx={{ p: 0 }}>
                        {error ? (
                            <Alert severity="error" sx={{ m: 2 }}>
                                {error}
                            </Alert>
                        ) : null}

                        {!loading && !error && visibleAttempts.length === 0 ? (
                            <Alert severity="info" sx={{ m: 2 }}>
                                No mock test attempts matched the current filters.
                            </Alert>
                        ) : null}

                        <Stack spacing={1.5} sx={{ p: 2, display: { xs: 'flex', xl: 'none' } }}>
                            {visibleAttempts.map((attempt) => {
                                const scoredCount = attempt.examTasks.filter((examTask) => examTask.score !== null).length;

                                return (
                                    <Card key={attempt.mockTestAttemptId} variant="outlined">
                                        <CardContent>
                                            <Stack spacing={2}>
                                                <Stack spacing={0.5}>
                                                    <Typography variant="subtitle1" sx={{ wordBreak: 'break-word' }}>
                                                        {attempt.mockTestAttemptId}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                                                        {attempt.userName}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: '#64748b', wordBreak: 'break-word' }}>
                                                        {attempt.userId || 'N/A'}
                                                    </Typography>
                                                </Stack>

                                                <Stack spacing={0.75}>
                                                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                                        {attempt.examName ? <Chip label={attempt.examName} size="small" /> : null}
                                                        {attempt.mockTestKind ? <Chip label={attempt.mockTestKind} size="small" /> : null}
                                                        {attempt.status ? (
                                                            <Chip
                                                                label={formatAttemptStatus(attempt.status)}
                                                                size="small"
                                                                color={getAttemptStatusChipColor(attempt.status)}
                                                            />
                                                        ) : null}
                                                        {renderAttemptSectionCategoryChips(attempt)}
                                                    </Stack>
                                                    <Typography variant="body2">Attempted: {formatDateTime(attempt.attemptedAt)}</Typography>
                                                    <Typography variant="body2">Last Attempted: {formatDateTime(attempt.lastAttemptedAt ?? '')}</Typography>
                                                    <Typography variant="body2">Exam Task Count: {attempt.examTasks.length}</Typography>
                                                    <Typography variant="body2">Scored: {scoredCount}</Typography>
                                                </Stack>

                                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                                                    <Button variant="outlined" onClick={() => handleOpenReviewDetail(attempt.mockTestAttemptId, attempt.userId)}>
                                                        {getAttemptActionLabel(attempt.status)}
                                                    </Button>
                                                </Stack>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </Stack>

                        <TableContainer sx={{ display: { xs: 'none', xl: 'block' } }}>
                            <Table sx={{ minWidth: 1180 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Attempt</TableCell>
                                        <TableCell>User</TableCell>
                                        <TableCell>Exam</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>{renderTimeSortLabel('attemptedAt', 'Attempted')}</TableCell>
                                        <TableCell>{renderTimeSortLabel('lastAttemptedAt', 'Last Attempted')}</TableCell>
                                        <TableCell>Exam Task Count</TableCell>
                                        <TableCell>Scored</TableCell>
                                        <DesktopActionHeaderCell>View</DesktopActionHeaderCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {visibleAttempts.map((attempt) => {
                                        const scoredCount = attempt.examTasks.filter((examTask) => examTask.score !== null).length;

                                        return (
                                            <TableRow hover key={attempt.mockTestAttemptId}>
                                                <TableCell sx={{ minWidth: 220 }}>
                                                    <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>
                                                        {attempt.mockTestAttemptId}
                                                    </Typography>
                                                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                                                        {attempt.examName ? <Chip label={attempt.examName} size="small" /> : null}
                                                        {attempt.mockTestKind ? <Chip label={attempt.mockTestKind} size="small" /> : null}
                                                        {attempt.status ? (
                                                            <Chip
                                                                label={formatAttemptStatus(attempt.status)}
                                                                size="small"
                                                                color={getAttemptStatusChipColor(attempt.status)}
                                                            />
                                                        ) : null}
                                                        {renderAttemptSectionCategoryChips(attempt)}
                                                    </Stack>
                                                </TableCell>
                                                <TableCell sx={{ minWidth: 220 }}>
                                                    <Typography sx={{ fontWeight: 700 }}>{attempt.userName}</Typography>
                                                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                                                        {attempt.userId || 'N/A'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography sx={{ fontWeight: 600 }}>{attempt.examName || 'N/A'}</Typography>
                                                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                                                        {attempt.mockTestKind || 'N/A'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ minWidth: 150 }}>
                                                    {attempt.status ? (
                                                        <Chip
                                                            label={formatAttemptStatus(attempt.status)}
                                                            size="small"
                                                            color={getAttemptStatusChipColor(attempt.status)}
                                                        />
                                                    ) : 'N/A'}
                                                </TableCell>
                                                <TableCell sx={{ minWidth: 180 }}>
                                                    {formatDateTime(attempt.attemptedAt)}
                                                </TableCell>
                                                <TableCell sx={{ minWidth: 180 }}>
                                                    {formatDateTime(attempt.lastAttemptedAt ?? '')}
                                                </TableCell>
                                                <TableCell>{attempt.examTasks.length}</TableCell>
                                                <TableCell>{scoredCount}</TableCell>
                                                <DesktopActionCell>
                                                    <Button variant="outlined" onClick={() => handleOpenReviewDetail(attempt.mockTestAttemptId, attempt.userId)}>
                                                        {getAttemptActionLabel(attempt.status)}
                                                    </Button>
                                                </DesktopActionCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Divider />
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            onPageChange={(_, nextPage) => setPage(nextPage)}
                            onRowsPerPageChange={(event) => {
                                setRowsPerPage(Number(event.target.value));
                                setPage(0);
                            }}
                            rowsPerPageOptions={[10, 20, 50, 100]}
                        />
                    </CardContent>
                </Card>
            </Stack>
        </>
    );
}
